import { useState } from "react";
import { Pressable, Text, ActivityIndicator, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import styled from "styled-components/native";
import { signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/lib/firebase";
import { removeItem } from "@/lib/storage";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmSheet from "@/components/ConfirmSheet";
import { colors, radius, font } from "@/lib/theme";
import StumpsIcon from "@/components/StumpsIcon";

const SafeArea = styled(SafeAreaView)`
  flex: 1;
  background-color: ${colors.white};
`;

const Center = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
  padding-horizontal: 24px;
`;

const Title = styled.Text`
  font-size: 22px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  letter-spacing: -0.5px;
  margin-bottom: 8px;
  margin-top: 24px;
  text-align: center;
`;

const Subtitle = styled.Text`
  font-size: 15px;
  color: ${colors.inkSecondary};
  margin-bottom: 32px;
  text-align: center;
  line-height: 22px;
`;

const SignOutBtn = styled.View`
  background-color: ${colors.bg};
  border-radius: ${radius.card}px;
  border-width: 1px;
  border-color: ${colors.line};
  padding-vertical: 14px;
  padding-horizontal: 32px;
`;

const SignOutText = styled.Text`
  font-size: 15px;
  color: ${colors.inkMuted};
  font-weight: ${font.semibold};
`;

const DeletingOverlay = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  justify-content: center;
  align-items: center;
`;

const DeletingText = styled.Text`
  font-size: 16px;
  font-weight: ${font.bold};
  color: ${colors.ink};
  margin-top: 16px;
`;

const DeletingSub = styled.Text`
  font-size: 14px;
  color: ${colors.inkMuted};
  margin-top: 4px;
`;

export default function OnboardingIndex() {
  const [deleting, setDeleting] = useState(false);
  const { confirmProps, confirm } = useConfirm();

  const handleDelete = async () => {
    const ok = await confirm({
      title: "Delete your account?",
      message: "This will permanently delete your account. This cannot be undone.",
      confirmLabel: "Delete account",
      destructive: true,
    });
    if (!ok) return;

    setDeleting(true);
    try {
      const deleteAccount = httpsCallable(functions, "deleteAccount");
      await deleteAccount({});
      await removeItem("activeClubId");
      await signOut(auth);
    } catch (err: any) {
      setDeleting(false);
      await confirm({
        title: "Error",
        message: err?.message || "Failed to delete account.",
        confirmLabel: "OK",
      });
    }
  };

  return (
    <SafeArea>
      <Center>
        <StumpsIcon size={48} />
        <Title>You're not part of a club yet</Title>
        <Subtitle>
          Ask your club admin to send you an invite. Once they do, you'll receive an email with a link to join.
        </Subtitle>
        <Pressable
          onPress={() => signOut(auth)}
          style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
        >
          <SignOutBtn>
            <SignOutText>Sign out</SignOutText>
          </SignOutBtn>
        </Pressable>
        <Pressable
          onPress={handleDelete}
          disabled={deleting}
          style={({ pressed }) => ({ marginTop: 16, opacity: pressed ? 0.7 : 1 })}
        >
          <Text style={{ fontSize: 14, color: colors.danger, fontWeight: "500" }}>Delete account</Text>
        </Pressable>
      </Center>

      {deleting && (
        <DeletingOverlay>
          <ActivityIndicator size="large" color={colors.danger} />
          <DeletingText>Deleting your account...</DeletingText>
          <DeletingSub>This may take a moment</DeletingSub>
        </DeletingOverlay>
      )}

      <ConfirmSheet {...confirmProps} />
    </SafeArea>
  );
}
